import { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot, doc, getDoc, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import i18n from '../i18n';
import { COLLECTIONS } from '../firebase/collections';
import { useAuth } from '../context/AuthContext';

export function useTaskCategories() {
  const { companyId } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    const q = query(
      collection(db, COLLECTIONS.TASK_CATEGORIES),
      where('companyId', '==', companyId),
      where('isActive', '==', true),
      orderBy('sortOrder')
    );
    return onSnapshot(q, (snap) => {
      setCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      console.error('useTaskCategories:', err);
      setLoading(false);
    });
  }, [companyId]);

  return { categories, loading };
}

export function useAllTaskCategories() {
  const { companyId } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    const q = query(
      collection(db, COLLECTIONS.TASK_CATEGORIES),
      where('companyId', '==', companyId),
      orderBy('sortOrder')
    );
    return onSnapshot(q, (snap) => {
      setCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      console.error('useAllTaskCategories:', err);
      setLoading(false);
    });
  }, [companyId]);

  return { categories, loading };
}

export function useTaskTags() {
  const { companyId } = useAuth();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    const q = query(
      collection(db, COLLECTIONS.TASK_TAGS),
      where('companyId', '==', companyId),
      where('isActive', '==', true),
      orderBy('sortOrder')
    );
    return onSnapshot(q, (snap) => {
      setTags(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      console.error('useTaskTags:', err);
      setLoading(false);
    });
  }, [companyId]);

  return { tags, loading };
}

export function useAllTaskTags() {
  const { companyId } = useAuth();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    const q = query(
      collection(db, COLLECTIONS.TASK_TAGS),
      where('companyId', '==', companyId),
      orderBy('sortOrder')
    );
    return onSnapshot(q, (snap) => {
      setTags(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      console.error('useAllTaskTags:', err);
      setLoading(false);
    });
  }, [companyId]);

  return { tags, loading };
}

export function useProductionAreas() {
  const { companyId } = useAuth();
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    const q = query(
      collection(db, COLLECTIONS.PRODUCTION_AREAS),
      where('companyId', '==', companyId),
      where('isActive', '==', true)
    );
    return onSnapshot(q, (snap) => {
      setAreas(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, [companyId]);

  return { areas, loading };
}

export function useAllProductionAreas() {
  const { companyId } = useAuth();
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    const q = query(
      collection(db, COLLECTIONS.PRODUCTION_AREAS),
      where('companyId', '==', companyId)
    );
    return onSnapshot(q, (snap) => {
      setAreas(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, [companyId]);

  return { areas, loading };
}

export function useCompanyUsers() {
  const { companyId } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    const q = query(
      collection(db, COLLECTIONS.USERS),
      where('companyId', '==', companyId)
    );
    return onSnapshot(q, (snap) => {
      setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, [companyId]);

  return { users, loading };
}

export function useCompanySettings() {
  const { companyId } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    return onSnapshot(doc(db, COLLECTIONS.SETTINGS, companyId), (snap) => {
      setSettings(snap.exists() ? snap.data() : null);
      setLoading(false);
    });
  }, [companyId]);

  return { settings, loading };
}

export function useFloorWorkers() {
  const { companyId } = useAuth();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    setError(null);
    // No orderBy — avoids requiring a composite index that may not be deployed.
    // Sort is applied client-side below.
    const q = query(
      collection(db, COLLECTIONS.FLOOR_WORKERS),
      where('companyId', '==', companyId)
    );
    return onSnapshot(q, (snap) => {
      const sorted = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setWorkers(sorted);
      setError(null);
      setLoading(false);
    }, (err) => {
      console.error('useFloorWorkers:', err);
      setError(err.message ?? i18n.t('loadErrors.workers'));
      setLoading(false);
    });
  }, [companyId]);

  return { workers, loading, error };
}

// Admin-only: per-floor-worker hourly rates, keyed by floorWorkers doc ID.
// Production users cannot read floorWorkerRates per Firestore rules, so this
// hook is only used on admin screens.
export function useFloorWorkerRates() {
  const { companyId } = useAuth();
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    setError(null);
    const q = query(
      collection(db, COLLECTIONS.FLOOR_WORKER_RATES),
      where('companyId', '==', companyId)
    );
    return onSnapshot(q, (snap) => {
      const map = {};
      snap.docs.forEach((d) => { map[d.id] = d.data(); });
      setRates(map);
      setError(null);
      setLoading(false);
    }, (err) => {
      console.error('useFloorWorkerRates:', err);
      setError(err.message ?? i18n.t('loadErrors.rates'));
      setLoading(false);
    });
  }, [companyId]);

  return { rates, loading, error };
}

// Admin-only: labour rate config (defaultTeamHourlyRate, currency).
// Production users cannot read adminSettings per Firestore rules.
export function useAdminSettings() {
  const { companyId } = useAuth();
  const [adminSettings, setAdminSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    return onSnapshot(doc(db, COLLECTIONS.ADMIN_SETTINGS, companyId), (snap) => {
      setAdminSettings(snap.exists() ? snap.data() : null);
      setLoading(false);
    });
  }, [companyId]);

  return { adminSettings, loading };
}
